/**
 * Session-scoped notification WebSocket singleton.
 *
 * Set `REACT_APP_NOTIFICATION_WS_URL` (e.g. `wss://…` or `ws://localhost:8000`) to enable.
 * When unset/empty, no socket is opened — use the Notifications page "Refresh" button.
 *
 * Lifecycle is owned by `NotificationWebSocketBridge` (App-level), not per-page layouts.
 * Consumers subscribe to parsed events; connect/disconnect are idempotent per user id.
 *
 * Server messages are expected to be JSON objects; legacy handlers treat `status: 'update'`
 * vs other payloads differently.
 */

const WS_URL = (process.env.REACT_APP_NOTIFICATION_WS_URL || '').trim();

/** @type {WebSocket | null} */
let socket = null;
/** @type {string | number | null} */
let connectUserId = null;
/** @type {Set<(kind: 'update' | 'message' | 'raw', payload: unknown) => void>} */
const subscribers = new Set();

const notifySubscribers = (kind, payload) => {
  subscribers.forEach((listener) => {
    try {
      listener(kind, payload);
    } catch {
      // ignore subscriber errors
    }
  });
};

const isSocketActiveForUser = (userId) =>
  socket != null && connectUserId === userId && socket.readyState <= WebSocket.OPEN;

export const isNotificationWebSocketEnabled = () => Boolean(WS_URL);

export const notificationWebSocket = {
  /**
   * @param {(kind: 'update' | 'message' | 'raw', payload: unknown) => void} listener
   * @returns {() => void} unsubscribe
   */
  subscribe(listener) {
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  },

  /**
   * Open (or keep) a socket for the authenticated user.
   * No-op when already connected for the same user id.
   *
   * @param {{ id: string | number } | null | undefined} user
   */
  connect(user) {
    if (!WS_URL || user == null || user.id == null) return;
    if (isSocketActiveForUser(user.id)) return;

    this.disconnect();

    connectUserId = user.id;
    const nextSocket = new WebSocket(WS_URL);
    socket = nextSocket;

    nextSocket.addEventListener('open', () => {
      try {
        nextSocket.send(JSON.stringify({ status: 'open', value: connectUserId }));
      } catch {
        // ignore
      }
    });

    nextSocket.addEventListener('message', (event) => {
      try {
        const value = JSON.parse(event.data);
        if (value?.status === 'update') {
          notifySubscribers('update', value);
        } else {
          notifySubscribers('message', value);
        }
      } catch {
        notifySubscribers('raw', event.data);
      }
    });
  },

  disconnect() {
    const activeSocket = socket;
    const activeUserId = connectUserId;
    socket = null;
    connectUserId = null;

    if (!activeSocket) return;

    try {
      if (activeSocket.readyState === WebSocket.OPEN && activeUserId != null) {
        activeSocket.send(JSON.stringify({ status: 'close', value: activeUserId }));
      }
    } catch {
      // ignore
    }
    try {
      activeSocket.close();
    } catch {
      // ignore
    }
  },

  /** @deprecated Prefer subscribe + connect. Kept for legacy call sites. */
  open(user, onEvent) {
    if (typeof onEvent === 'function') {
      this.subscribe(onEvent);
    }
    this.connect(user);
  },

  /** Alias for disconnect — used on logout / beforeunload. */
  close() {
    this.disconnect();
  },
};
